import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Button from '@material-ui/core/Button';
import { onboardingTasks, taskRegistry, resolveStaffDisplayName } from '@ourbranch/onboarding-tasks';
import { IconButton, Menu, MenuItem } from '@material-ui/core';

import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { capitalize, dateTimeFormatter } from 'core/helpers/formatters';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import viewTask from 'core/assets/svg/view-task.svg';
import infoIcon from 'core/assets/svg/info-icon-dark.svg';
import pendingIcon from 'core/assets/svg/clock-filled.svg';
import documentApprovalIcon from 'core/assets/svg/document-approval.svg';
import remove from 'core/assets/svg/remove.svg';
import { DocumentModal } from 'core/components/document-upload-modal';

import undoIcon from 'customer/assets/undo.svg';
import uploadIcon from 'customer/assets/uploadIcon.svg';
import completedIcon from 'customer/assets/completed.svg';
import questionIcon from 'customer/assets/questionIcon.svg';
import { downloadTaskDocument } from 'customer/helpers/policy-document-service';
import { updateTaskData } from 'customer/helpers/task-mutations';

import ResetModal from '../reset-task/reset-task-modal';
import ApprovalModal from '../approve-task/approve-task-modal';
import { processingReviewTasks } from 'core/helpers/onboarding-task-helpers';
import MIPipOption4LearnMoreModal from '../mi-pip-option4-learn-more-modal/mi-pip-option4-learn-more-modal';
import VerifyRoofYearLearnMoreModal from '../verify-roof-year-learn-more-modal/verify-roof-year-learn-more-modal';

import useStyles from './task.styles';
import CancelModal from '../cancel-task/cancel-task-modal';

const Task = ({
  taskId,
  taskName,
  data,
  completed,
  completedDate,
  policyId,
  readOnly,
  completedBy,
  rejections,
  approvalDate,
  approvedBy,
  onTaskApproved
}) => {
  const classes = useStyles();
  const {
    account: { id, completeTask, resetTask, approveTask, cancelTask, fetchFullAccount }
  } = useStore();
  const { canResetOnboardingTasks, isAgency, canCancelOnboardingTasks } = useSession();
  const toast = useToast();
  const { person, customer, documentType, files, documentUrl: singleDocUrl } = data || {};
  const driver = data?.driver ? `${data.driver.firstName} ${data.driver.lastName}` : person || customer;
  const documentUrl = singleDocUrl ? singleDocUrl : files?.length === 1 ? files?.[0]?.documentUrl : null;

  const [showResetModal, setShowResetModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showMIPipOption4Modal, setShowMIPipOption4Modal] = useState(false);
  const [showVerifyRoofYearModal, setShowVerifyRoofYearModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [, type] = policyId.split('-');
  const autoApproved = approvedBy === 'Auto Approved' || (approvalDate && approvalDate === completedDate);
  // Tasks that don't support file upload (e.g., Community Drive Registration, completed via mobile app)
  // hide the upload CTA and document modal. Defaults to true when omitted from the task config.
  const supportsFileUpload = taskRegistry[taskName]?.supportsFileUpload !== false;
  const displayName = resolveStaffDisplayName(taskName, data);

  const handleReset = async () => {
    const res = await resetTask({
      accountId: id,
      taskId,
      policyId,
      taskName,
      completedBy,
      rejections
    });
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been reset. `
      });
    } else {
      toast.notify({
        type: 'error',
        message: `There was an error resetting the task - ${res.error}`
      });
    }
  };

  const handleCancel = async () => {
    const res = await cancelTask({
      accountId: id,
      taskId,
      policyId,
      taskName,
      completedBy
    });
    setShowCancelModal(false);
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been cancelled.`
      });
    } else {
      toast.notify({
        type: 'error',
        message: 'There was an error cancelling the task, please try again.'
      });
    }
  };

  const handleApprove = async () => {
    const res = await approveTask({
      taskId,
      accountId: id,
      policyId
    });
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The "${taskName}" task has been approved.`
      });
      // Call the callback to keep the submitted section open
      if (onTaskApproved) {
        onTaskApproved();
      }
    } else {
      toast.notify({
        type: 'error',
        message: `There was an error approving the task - ${res.error}`
      });
    }
  };

  const showResetTaskIcon =
    canResetOnboardingTasks &&
    !(taskName === onboardingTasks.CompleteInventory) &&
    !(completedBy === 'member' && taskName === onboardingTasks.SignApplication);

  const handleViewFile = useCallback(
    (event) => {
      if (documentUrl) {
        downloadTaskDocument(documentUrl, id);
      } else if (files?.length > 1) {
        setAnchorEl(event.currentTarget);
      }
    },
    [documentUrl, files, id]
  );

  const handleCloseFileMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Get Appcues modal ID from taskRegistry - some tasks use custom modals with dynamic data
  const isOption4 = taskName === onboardingTasks.ProofOfMIPipOption4;
  const isVerifyRoofYear = taskName === onboardingTasks.VerifyRoofYear;
  const appcuesModalId = taskRegistry[taskName]?.appcuesModalId;
  const hasLearnMore = isOption4 || isVerifyRoofYear || appcuesModalId;
  const appCuesOnClick = () => {
    window?.Appcues?.show(appcuesModalId);
  };

  return (
    <div className={classNames(classes.container, { [classes.completed]: completed })}>
      <div className={classes.task}>
        {completed &&
          (approvalDate ? (
            <img alt="rounded checkmark" className={classes.completedIcon} src={completedIcon} />
          ) : (
            <img alt="review pending" className={classes.pendingIcon} src={pendingIcon} />
          ))}
        <div>
          <div className={classNames(classes.taskInfo, { [classes.taskInfoCompleted]: completed })}>
            <div className={classes.taskNameContainer}>
              <Label type="infoValue" className={classNames(classes.taskName, { [classes.completedTask]: completed })}>
                {displayName}
              </Label>
              {hasLearnMore && (
                <IconButton
                  className={classes.learnMoreLink}
                  onClick={() => {
                    if (isOption4) {
                      setShowMIPipOption4Modal(true);
                    } else if (isVerifyRoofYear) {
                      setShowVerifyRoofYearModal(true);
                    } else {
                      window?.Appcues?.show(appcuesModalId);
                    }
                  }}
                >
                  <div className={classes.learnMoreLinkText}>Learn More</div>
                  <img src={arrowIcon} height={14} width={14} alt="Learn More" />
                </IconButton>
              )}
            </div>
            {completedDate && (
              <Label variant="darkGreenSmall" className={classes.completedAt}>
                Submitted on {dateTimeFormatter(completedDate)} EST by {completedBy}
              </Label>
            )}
          </div>
          <div className={classes.tags}>
            <Tag className={classes.tag}>
              {capitalize(type)}
              {driver ? ` - ${driver}` : ''}
            </Tag>
            {completedDate && (
              <Tag
                className={classNames(classes.tag, classes.statusTag)}
                id={approvalDate ? 'task_approved_badge' : 'task_review_pending_badge'}
              >
                <img alt="status icon" src={approvalDate ? completedIcon : pendingIcon} />
                {approvalDate ? (autoApproved ? 'Auto-approved' : 'Approved') : 'Review pending'}
                {((approvalDate && autoApproved) || !approvalDate) && (
                  <IconButtonWithTooltip
                    icon={questionIcon}
                    iconClassName={classes.questionIcon}
                    tooltip={
                      approvalDate && autoApproved
                        ? "Because of the task type or details provided, this task is auto-approved and doesn't need Branch staff to review it."
                        : 'Please allow up to 3 business days for this task to be reviewed.'
                    }
                  />
                )}
              </Tag>
            )}
          </div>
        </div>
      </div>
      {completed ? (
        <div className={classes.task}>
          <div className={classes.iconButtons}>
            {showResetTaskIcon && (
              <Button onClick={() => setShowResetModal(true)} className={classes.actionButton}>
                <img src={undoIcon} alt="undo" className={classes.actionButtonIcon} />
                Reset
              </Button>
            )}
            {(documentUrl || files?.length) && (
              <Button onClick={handleViewFile} className={classes.actionButton}>
                <img src={viewTask} alt="view file" className={classes.actionButtonIcon} />
                {files?.length > 1 ? `View ${files.length} files` : 'View file'}
              </Button>
            )}
            {files && files?.length > 1 && (
              <Menu
                id="tooltip-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseFileMenu}
              >
                {files.map((f, index) => {
                  const fullFileName =
                    typeof f.documentUrl === 'string' ? f.documentUrl.split('/').pop() : f.fileName || 'Unknown file';
                  const displayName = fullFileName
                    ? fullFileName.replace(`${taskName} - `, '').replace(`${taskName}-`, '')
                    : 'Unknown file';

                  return (
                    <MenuItem
                      key={index}
                      onClick={() => downloadTaskDocument(f.documentUrl, id)}
                      className={classes.fileMenuItem}
                    >
                      {displayName}
                    </MenuItem>
                  );
                })}
                <MenuItem
                  key="view-all"
                  onClick={() => {
                    files.forEach((f) => downloadTaskDocument(f.documentUrl, id));
                    handleCloseFileMenu();
                  }}
                >
                  View all documents
                </MenuItem>
              </Menu>
            )}
            {canCancelOnboardingTasks && (
              <Button
                onClick={() => {
                  setShowCancelModal(true);
                }}
                disableFocusRipple
                className={classes.upload}
                variant="text"
                color="secondary"
              >
                <img alt="cancel task" className={classNames(classes.removeIcon)} src={remove} />
                Cancel Task
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!readOnly && (
            <div className={classes.iconButtons}>
              {supportsFileUpload && (
                <Button
                  onClick={() => {
                    setShowUploadModal(true);
                  }}
                  disableFocusRipple
                  className={classes.upload}
                  variant="text"
                  color="secondary"
                >
                  <img alt="upload icon" className={classNames(classes.uploadIcon)} src={uploadIcon} />
                  Upload and submit
                </Button>
              )}
              {canCancelOnboardingTasks && (
                <Button
                  onClick={() => {
                    setShowCancelModal(true);
                  }}
                  disableFocusRipple
                  className={classes.upload}
                  variant="text"
                  color="secondary"
                >
                  <img alt="cancel task" className={classNames(classes.removeIcon)} src={remove} />
                  Cancel Task
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {canResetOnboardingTasks && completed && !approvalDate && (
        <div className={classes.approvalSection}>
          <img src={infoIcon} alt="approval icon" />
          <div className={classNames(classes.column, classes.approvalContent)}>
            <Label type="body3" fontWeight={600}>
              This task requires your review.
            </Label>
            <Label type="body3">
              Review the documents and either approve or reset the task. Resetting requests new documentation from the
              member and, if applicable, their agent.
            </Label>
          </div>
          <div className={classes.column}>
            <Button onClick={() => setShowApproveModal(true)} className={classes.approvalButton}>
              <img src={documentApprovalIcon} alt="undo" />
              Approve
            </Button>
          </div>
        </div>
      )}

      <ResetModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        taskName={taskName}
        onReset={handleReset}
        completedBy={completedBy}
        rejections={rejections}
      />
      <ApprovalModal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        taskName={taskName}
        onApprove={handleApprove}
        completedBy={completedBy}
        rejections={rejections}
      />
      <MIPipOption4LearnMoreModal
        open={showMIPipOption4Modal}
        onClose={() => setShowMIPipOption4Modal(false)}
        data={data}
      />
      <VerifyRoofYearLearnMoreModal
        open={showVerifyRoofYearModal}
        onClose={() => setShowVerifyRoofYearModal(false)}
        data={data}
      />

      {supportsFileUpload && (
        <DocumentModal
          open={showUploadModal}
          onClose={async () => {
            setShowUploadModal(false);
            await fetchFullAccount(id);
          }}
          documents={files || []}
          taskId={taskId}
          accountId={id}
          onUpdateTaskData={updateTaskData}
          completeTask={completeTask}
          taskName={taskName}
          policyId={policyId}
          data={data}
          driver={driver}
          documentType={documentType}
          isAgency={isAgency}
          processingReviewTasks={processingReviewTasks}
          appCuesOnClick={appCuesOnClick}
        />
      )}
      <CancelModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        taskName={taskName}
        onCancel={handleCancel}
      />
    </div>
  );
};

Task.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskName: PropTypes.string.isRequired,
  data: PropTypes.object,
  completed: PropTypes.bool.isRequired,
  completedDate: PropTypes.string,
  readOnly: PropTypes.bool,
  completedBy: PropTypes.string,
  approvalDate: PropTypes.string,
  approvedBy: PropTypes.string,
  rejections: PropTypes.array,
  policyId: PropTypes.string,
  onTaskApproved: PropTypes.func
};

Task.defaultProps = {
  data: {},
  completedDate: null,
  approvalDate: false,
  readOnly: false
};

export default observer(Task);
